import { AlertCircle } from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'

import { Label } from '@/components/ui/label'
import type { FileUploadConfig } from '@/types/api/file'
import type { FormField } from '@/types/api/form'
import { FormFieldType } from '@/types/enums'
import { cn } from '@/utils/cn'
import { validateFieldValue } from '@/utils/field-formatters'

import { fillInFields, isDataField } from '../utils/fieldAccess'
import { isFieldVisible, valuesByKey } from '../utils/fieldConditions'
import { fieldWidth, isLayoutField } from '../utils/layoutField'
import { defaultValues } from '../utils/prefill'

import type { FileUploadFn } from './FileFieldInput'
import { FieldHelpText, LayoutFieldView } from './LayoutFieldView'
import { RecordFieldInput, choiceOptions } from './RecordFieldInput'

/**
 * The fields of the "New Record" form, shared by the create dialog and the form designer's preview
 * so the preview is exactly what people will fill in.
 */

/**
 * Width and column count by number of fields, so short forms stay compact and long ones use the space.
 * `dialog` sizes the modal from the small breakpoint up; `panel` is the same width for an in-page preview.
 */
export function layoutFor(fieldCount: number): { dialog: string; panel: string; grid: string } {
  if (fieldCount <= 6) return { dialog: 'sm:max-w-lg', panel: 'max-w-lg', grid: 'grid-cols-1' }
  if (fieldCount <= 14) return { dialog: 'sm:max-w-3xl', panel: 'max-w-3xl', grid: 'grid-cols-1 md:grid-cols-2' }
  return { dialog: 'sm:max-w-5xl', panel: 'max-w-5xl', grid: 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3' }
}

/** Fields that need the whole row: long choice lists. Files share a row; their chips wrap into "+N more". */
function spansFullRow(field: FormField): boolean {
  const width = fieldWidth(field)
  if (width) return width === 'full'
  if (field.type === FormFieldType.PARAGRAPH) return true
  if (field.type === FormFieldType.RADIO || field.type === FormFieldType.CHECKBOX) return choiceOptions(field).length > 4
  return false
}

/** Scrolls a field into view and focuses its first focusable control. */
function focusField(id: string) {
  const el = document.getElementById(id)
  if (!el) return
  const target = el.matches('input,button,select,textarea') ? el : el.querySelector<HTMLElement>('input,button,select,textarea')
  el.scrollIntoView({ block: 'center', behavior: 'smooth' })
  ;(target ?? el).focus({ preventScroll: true })
}

/** Answers, errors and the "Show when…" rules of a create form. */
export function useRecordForm(fields: readonly FormField[] | undefined) {
  const [typed, setTyped] = useState<Record<string, string>>({})
  const [errors, setErrors] = useState<Record<string, string | null>>({})

  // Fields the user cannot edit are not in the form: the server fills nothing in for them
  const shownFields = useMemo(
    () => (fields ? fillInFields(fields).sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0)) : []),
    [fields]
  )
  // Layout elements are shown but hold no value
  const dataFields = useMemo(() => shownFields.filter(isDataField), [shownFields])
  // Default choices count as answers until the user changes them
  const values = useMemo(() => ({ ...defaultValues(dataFields), ...typed }), [dataFields, typed])
  // "Show when…" rules: hidden fields are not shown and never block the create
  const answers = useMemo(() => valuesByKey(dataFields, (f) => values[f.id]), [dataFields, values])
  const visibleFields = useMemo(() => shownFields.filter((f) => isFieldVisible(f, answers)), [shownFields, answers])
  const visibleDataFields = useMemo(() => visibleFields.filter(isDataField), [visibleFields])

  const change = useCallback((fieldId: string, value: string) => {
    setTyped((prev) => ({ ...prev, [fieldId]: value }))
    setErrors((prev) => ({ ...prev, [fieldId]: null }))
  }, [])

  const reset = useCallback(() => {
    setTyped({})
    setErrors({})
  }, [])

  /** Checks the visible fields, shows their errors and focuses the first one; true when all pass. */
  const validate = (): boolean => {
    if (!dataFields.length) return false
    const found: Record<string, string | null> = {}
    for (const field of visibleDataFields) {
      found[field.id] = validateFieldValue(values[field.id] ?? '', field.type as FormFieldType, field.required, field.validations, field.options)
    }
    setErrors(found)
    const firstInvalid = visibleDataFields.find((f) => found[f.id])
    if (firstInvalid) focusField(firstInvalid.id)
    return !firstInvalid
  }

  /** What to send. An answer typed before a rule hid its field is left out: the user no longer sees it. */
  const payload = () => visibleDataFields
    .filter((f) => (values[f.id] ?? '') !== '')
    .map((f) => ({ formFieldId: f.id, value: values[f.id] }))

  const requiredLeft = visibleDataFields.filter((f) => f.required && !(values[f.id] ?? '').trim()).length
  const requiredStatus = !visibleDataFields.some((f) => f.required)
    ? ''
    : requiredLeft > 0
      ? `${requiredLeft} required field${requiredLeft === 1 ? '' : 's'} left`
      : 'All required fields filled'

  return {
    values, errors, dataFields, visibleFields, visibleDataFields, change, reset, validate, payload, requiredStatus,
    layout: layoutFor(dataFields.length),
  }
}

export type RecordFormState = ReturnType<typeof useRecordForm>

/** The field grid of the create form. Without `uploadFile`, file fields show a read-only placeholder. */
export function RecordFormFields({ form, disabled, uploadFile, fileConfig, onUploadingChange }: {
  form: RecordFormState
  disabled?: boolean
  uploadFile?: (field: FormField) => FileUploadFn
  fileConfig?: Pick<FileUploadConfig, 'maxFileSizeBytes' | 'allowedTypes'> | null
  onUploadingChange?: (fieldId: string, open: boolean) => void
}) {
  const answers = valuesByKey(form.dataFields, (f) => form.values[f.id])
  return (
    <div className={cn('grid gap-x-4', form.layout.grid)}>
      {form.visibleFields.map((field) => isLayoutField(field) ? (
        // A line break is an empty full-width cell with no padding, so the next field starts a new row
        <div key={field.id} className={cn('col-span-full', field.type !== FormFieldType.LINE_BREAK && 'pb-5')}>
          <LayoutFieldView field={field} />
        </div>
      ) : (
        <div key={field.id} className={cn('min-w-0 space-y-1.5 pb-5', spansFullRow(field) && 'col-span-full')}>
          <Label htmlFor={field.id} className="flex items-center gap-1.5">
            {field.label}
            {field.required && (
              <span className="text-destructive-500 text-sm leading-none" aria-hidden="true">*</span>
            )}
            {field.required && <span className="sr-only">(required)</span>}
          </Label>
          <RecordFieldInput
            field={field}
            value={form.values[field.id] ?? ''}
            error={form.errors[field.id] ?? null}
            onChange={(v) => form.change(field.id, v)}
            disabled={disabled}
            uploadFile={uploadFile?.(field)}
            fileConfig={fileConfig}
            onUploadingChange={onUploadingChange && ((open) => onUploadingChange(field.id, open))}
            valuesByKey={answers}
          />
          <FieldHelpText field={field} />
          {form.errors[field.id] && (
            <p id={`${field.id}-error`} className="flex items-center gap-1 text-xs text-destructive-600">
              <AlertCircle className="h-3 w-3" />
              {form.errors[field.id]}
            </p>
          )}
        </div>
      ))}
    </div>
  )
}
